// src/pages/ConditionsGeneral.jsx
import * as React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Divider,
} from '@mui/material';

import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

// i18n
import { useI18n } from '../i18n/I18nProvider';
import conditionsI18n from '../i18n/pages/conditionsgeneralpage.i18n';

const BLUE = '#0466c8';
const BLUE_BG = 'rgba(11,95,255,0.08)';

const Strong = ({ children }) => (
  <Box component="span" sx={{ fontWeight: 800 }}>{children}</Box>
);

export default function ConditionsGeneralPage() {
  const { t, register } = useI18n();
  React.useEffect(() => {
    register('conditions', conditionsI18n);
  }, [register]);

  return (
    <>
      <Navbar />

      {/* HERO */}
      <Box sx={{ bgcolor: BLUE, color: '#fff', py: { xs: 4, md: 6 } }}>
        <Container maxWidth="lg">
          <Typography
            component="h1"
            sx={{
              fontWeight: 900,
              fontSize: { xs: 26, md: 36 },
              lineHeight: 1.15,
              mb: 1,
            }}
          >
            {t('conditions.hero.title')}
          </Typography>
          <Typography sx={{ opacity: 0.95, fontSize: { xs: 15.5, md: 17 } }}>
            {t('conditions.hero.desc')}
          </Typography>
        </Container>
      </Box>

      {/* CONTENU */}
      <Box sx={{ bgcolor: '#fff' }}>
        <Container maxWidth="lg" sx={{ py: { xs: 3, md: 4 } }}>
          <Paper
            variant="outlined"
            sx={{
              p: { xs: 2, md: 2.5 },
              borderRadius: 3,
              borderColor: '#e8efe9',
            }}
          >
            {/* Intro courte */}
            <Box
              sx={{
                mb: 2.5,
                p: 1.5,
                borderRadius: 2,
                bgcolor: BLUE_BG,
              }}
            >
              <Typography sx={{ fontWeight: 800, mb: 0.5 }}>
                {t('conditions.intro.highlightTitle')}
              </Typography>
              <Typography sx={{ color: 'text.secondary', fontSize: 14 }}>
                {t('conditions.intro.highlightText')}
              </Typography>
            </Box>

            {/* ARTICLE 1 */}
            <Box sx={{ mb: 2.5 }}>
              <Typography sx={{ fontWeight: 900, mb: 0.5 }}>
                {t('conditions.article1.title')}
              </Typography>
              <Typography sx={{ color: 'text.secondary' }}>
                {t('conditions.article1.p1')}
              </Typography>
              <Typography sx={{ color: 'text.secondary', mt: 1 }}>
                {t('conditions.article1.p2')}
              </Typography>
              <Typography sx={{ color: 'text.secondary', mt: 1 }}>
                {t('conditions.article1.p3')}
              </Typography>
            </Box>

            <Divider sx={{ my: 1.75 }} />

            {/* ARTICLE 2 */}
            <Box sx={{ mb: 2.5 }}>
              <Typography sx={{ fontWeight: 900, mb: 0.5 }}>
                {t('conditions.article2.title')}
              </Typography>
              <Typography sx={{ color: 'text.secondary', mb: 1 }}>
                <Strong>{t('conditions.article2.sellerTitle')}</Strong>{' '}
                {t('conditions.article2.sellerText')}
              </Typography>
              <Typography sx={{ color: 'text.secondary', mb: 1 }}>
                <Strong>{t('conditions.article2.productTitle')}</Strong>{' '}
                {t('conditions.article2.productText')}
              </Typography>
              <Typography sx={{ color: 'text.secondary' }}>
                <Strong>{t('conditions.article2.saleTitle')}</Strong>{' '}
                {t('conditions.article2.saleText')}
              </Typography>
            </Box>

            <Divider sx={{ my: 1.75 }} />

            {/* ARTICLE 3 */}
            <Box sx={{ mb: 2.5 }}>
              <Typography sx={{ fontWeight: 900, mb: 0.5 }}>
                {t('conditions.article3.title')}
              </Typography>
              <Typography sx={{ color: 'text.secondary' }}>
                {t('conditions.article3.p1')}
              </Typography>
            </Box>

            <Divider sx={{ my: 1.75 }} />

            {/* ARTICLE 4 */}
            <Box sx={{ mb: 2.5 }}>
              <Typography sx={{ fontWeight: 900, mb: 0.5 }}>
                {t('conditions.article4.title')}
              </Typography>

              <Typography sx={{ color: 'text.secondary', mb: 1 }}>
                <Strong>{t('conditions.article4.step1Title')}</Strong>
              </Typography>
              <Typography sx={{ color: 'text.secondary' }}>
                {t('conditions.article4.step1P1')}
              </Typography>
              <Typography sx={{ color: 'text.secondary', mt: 1 }}>
                {t('conditions.article4.step1P2')}
              </Typography>

              <Typography sx={{ color: 'text.secondary', mt: 2, mb: 1 }}>
                <Strong>{t('conditions.article4.step2Title')}</Strong>
              </Typography>
              <Typography sx={{ color: 'text.secondary' }}>
                {t('conditions.article4.step2P1')}
              </Typography>

              <Typography sx={{ color: 'text.secondary', mt: 1 }}>
                <Strong>{t('conditions.article4.okTitle')}</Strong>{' '}
                {t('conditions.article4.okText')}
              </Typography>
              <Typography sx={{ color: 'text.secondary', mt: 1 }}>
                <Strong>{t('conditions.article4.hsTitle')}</Strong>{' '}
                {t('conditions.article4.hsText1')}
              </Typography>
              <Typography sx={{ color: 'text.secondary', mt: 1 }}>
                {t('conditions.article4.hsText2')}
              </Typography>
              <Typography sx={{ color: 'text.secondary', mt: 1 }}>
                {t('conditions.article4.hsText3')}
              </Typography>

              <Typography sx={{ color: 'text.secondary', mt: 2, mb: 1 }}>
                <Strong>{t('conditions.article4.step3Title')}</Strong>
              </Typography>
              <Typography sx={{ color: 'text.secondary' }}>
                {t('conditions.article4.step3P1')}
              </Typography>
            </Box>

            <Divider sx={{ my: 1.75 }} />

            {/* ARTICLE 5 */}
            <Box sx={{ mb: 2.5 }}>
              <Typography sx={{ fontWeight: 900, mb: 0.5 }}>
                {t('conditions.article5.title')}
              </Typography>
              <Typography sx={{ color: 'text.secondary' }}>
                {t('conditions.article5.p1')}
              </Typography>
              <Typography sx={{ color: 'text.secondary', mt: 1 }}>
                {t('conditions.article5.p2')}
              </Typography>
            </Box>

            <Divider sx={{ my: 1.75 }} />

            {/* ARTICLE 6 */}
            <Box sx={{ mb: 2.5 }}>
              <Typography sx={{ fontWeight: 900, mb: 0.5 }}>
                {t('conditions.article6.title')}
              </Typography>
              <Typography sx={{ color: 'text.secondary', mb: 1 }}>
                {t('conditions.article6.p1')}
              </Typography>
              <Typography sx={{ color: 'text.secondary', mb: 1 }}>
                {t('conditions.article6.p2')}
              </Typography>
              <Typography sx={{ color: 'text.secondary' }}>
                {t('conditions.article6.p3')}
              </Typography>
            </Box>

            <Divider sx={{ my: 1.75 }} />

            {/* ARTICLE 7 */}
            <Box sx={{ mb: 2.5 }}>
              <Typography sx={{ fontWeight: 900, mb: 0.5 }}>
                {t('conditions.article7.title')}
              </Typography>
              <Typography sx={{ color: 'text.secondary', mb: 1 }}>
                {t('conditions.article7.p1')}
              </Typography>
              <Typography sx={{ color: 'text.secondary' }}>
                {t('conditions.article7.p2')}
              </Typography>
            </Box>

            <Divider sx={{ my: 1.75 }} />

            {/* ARTICLE 8 */}
            <Box sx={{ mb: 2.5 }}>
              <Typography sx={{ fontWeight: 900, mb: 0.5 }}>
                {t('conditions.article8.title')}
              </Typography>
              <Typography sx={{ color: 'text.secondary' }}>
                {t('conditions.article8.p1')}
              </Typography>
            </Box>

            <Divider sx={{ my: 1.75 }} />

            {/* ARTICLE 9 */}
            <Box sx={{ mb: 2.5 }}>
              <Typography sx={{ fontWeight: 900, mb: 0.5 }}>
                {t('conditions.article9.title')}
              </Typography>
              <Typography sx={{ color: 'text.secondary' }}>
                {t('conditions.article9.p1')}
              </Typography>
            </Box>

            <Divider sx={{ my: 1.75 }} />

            {/* ARTICLE 10 */}
            <Box sx={{ mb: 2.5 }}>
              <Typography sx={{ fontWeight: 900, mb: 0.5 }}>
                {t('conditions.article10.title')}
              </Typography>
              <Typography sx={{ color: 'text.secondary', mb: 1 }}>
                {t('conditions.article10.p1')}
              </Typography>
              <Typography sx={{ color: 'text.secondary' }}>
                {t('conditions.article10.p2')}
              </Typography>
            </Box>

            <Divider sx={{ my: 1.75 }} />

            {/* ARTICLE 11 */}
            <Box sx={{ mb: 2.5 }}>
              <Typography sx={{ fontWeight: 900, mb: 0.5 }}>
                {t('conditions.article11.title')}
              </Typography>
              <Typography sx={{ color: 'text.secondary', mb: 1 }}>
                {t('conditions.article11.p1')}
              </Typography>
              <Typography sx={{ color: 'text.secondary' }}>
                {t('conditions.article11.p2')}
              </Typography>
            </Box>

            <Divider sx={{ my: 1.75 }} />

            {/* ARTICLE 12 */}
            <Box sx={{ mb: 2.5 }}>
              <Typography sx={{ fontWeight: 900, mb: 0.5 }}>
                {t('conditions.article12.title')}
              </Typography>
              <Typography sx={{ color: 'text.secondary', mb: 1 }}>
                {t('conditions.article12.p1')}
              </Typography>
              <Typography sx={{ color: 'text.secondary' }}>
                {t('conditions.article12.p2')}
              </Typography>
            </Box>

            <Divider sx={{ my: 1.75 }} />

            {/* ARTICLE 13 */}
            <Box sx={{ mb: 2.5 }}>
              <Typography sx={{ fontWeight: 900, mb: 0.5 }}>
                {t('conditions.article13.title')}
              </Typography>
              <Typography sx={{ color: 'text.secondary' }}>
                {t('conditions.article13.p1')}
              </Typography>
            </Box>

            <Divider sx={{ my: 1.75 }} />

            {/* ARTICLE 14 */}
            <Box>
              <Typography sx={{ fontWeight: 900, mb: 0.5 }}>
                {t('conditions.article14.title')}
              </Typography>
              <Typography sx={{ color: 'text.secondary' }}>
                {t('conditions.article14.p1')}
              </Typography>
            </Box>
          </Paper>
        </Container>
      </Box>

      <Footer />
    </>
  );
}